import {
  ActionMenu,
  Button,
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  PersonLabel,
  StatCard,
  StatRow,
  Table,
} from "@gladis/ui";
import type { MemberDto } from "@gladis/shared";
import { useState, type ReactNode } from "react";

import type { TransactionDto } from "../dto.js";
import { formatDate, formatGain, formatMoney, formatPrice, formatShares } from "../format.js";
import {
  useDeletePrice,
  useDeleteTransaction,
  useEditTransaction,
  useStockDetail,
} from "../hooks.js";
import { ConfirmDialog } from "./ConfirmDialog.js";
import { PriceChart, type PriceChartMode } from "./PriceChart.js";
import { StockActions } from "./StockActions.js";
import { TransactionForm } from "./TransactionForm.js";
import { useT } from "@gladis/ui/i18n";

/**
 * Everything about one holding.
 *
 * ==========================================================================================
 * `canBeRemoved` AND `canDelete` COME FROM THE SERVER
 * ==========================================================================================
 *
 * Neither is re-derived here, and that is deliberate rather than lazy.
 *
 * `canBeRemoved` counts SOFT-DELETED transactions, which this view never receives — the history
 * below shows live rows only. A client deriving it from that list would get the opposite answer for
 * a stock whose every transaction was deleted, and would offer a Remove button that always fails.
 *
 * `canDelete` is `source = 'member'`. Deriving it from `recordedBy !== null` would be wrong the
 * moment a member leaves the household, because the column goes null and the price is still theirs.
 */

/**
 * How many price rows the history shows before it stops.
 *
 * Once 011's nightly job runs, a stock held for a year has around 260 of them and a table of that
 * length is not a history anybody reads — it is a wall between the transactions above it and
 * everything below. Twenty-five is about a month of trading days: enough to see the recent shape,
 * short enough to scroll past.
 *
 * The count is stated rather than implied, and the rest is one click away. A silent truncation would
 * make "is my backfill in there?" unanswerable.
 */
const PRICE_ROWS = 25;

export interface StockDetailProps {
  stockId: string;
  onRemoved: () => void;
  /**
   * The household's members, for their faces (020).
   *
   * This is the ONE page in the application that gains a request for this feature. Every other
   * surface reads the member list its page already fetched; investments never needed one, because
   * its rows name people by label alone. FR-025 was amended to permit exactly this: one
   * constant-cost fetch per page, never one per row.
   */
  members?: readonly MemberDto[];
}

export function StockDetail({ stockId, onRemoved, members = [] }: StockDetailProps): ReactNode {
  const t = useT();
  const detail = useStockDetail(stockId);

  const editTransaction = useEditTransaction();
  const deleteTransaction = useDeleteTransaction();
  const deletePrice = useDeletePrice();

  const [editingTxn, setEditingTxn] = useState<TransactionDto | null>(null);
  const [deletingTxn, setDeletingTxn] = useState<TransactionDto | null>(null);
  const [deletingPrice, setDeletingPrice] = useState<string | null>(null);
  const [allPrices, setAllPrices] = useState(false);
  const [chartMode, setChartMode] = useState<PriceChartMode>("price");

  /*
   * A 404 IS NOT AN ERROR, IT IS AN ANSWER (018's FR-016).
   *
   * Until the activity feed existed, this page was only ever reached by clicking a row in a list, so the
   * record was there by construction. The feed can now link to a record that has since been hard-deleted —
   * in practice a stock removed while it had no transactions — and the feed deliberately does not check
   * (that would be a query per row on every page). So the destination reports it, and "we couldn't load
   * this" is the wrong sentence for a record that is simply gone.
   */
  if (detail.isPending) return <LoadingState>{t("inv.detail.loading")}</LoadingState>;
  if (detail.error?.status === 404) return <EmptyState>{t("inv.detail.gone")}</EmptyState>;
  if (detail.isError) return <ErrorState>{t("inv.detail.loadFailed")}</ErrorState>;

  const { stock, position, transactions, prices } = detail.data;
  const visiblePrices = allPrices ? prices : prices.slice(0, PRICE_ROWS);
  const unpriced = position.currentValue === null;

  return (
    <div className="flex flex-col gap-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{stock.name}</h1>
          <p className="text-muted text-sm">
            {stock.symbol}
            {stock.exchange ? ` · ${stock.exchange}` : ""} · added by{" "}
            <PersonLabel
              className="align-middle"
              label={stock.addedBy}
              isPerson={stock.addedById !== null}
              avatarUrl={members.find((m) => m.userId === stock.addedById)?.avatarUrl}
            />
          </p>
        </div>
        <StockActions
          stockId={stockId}
          name={stock.name}
          symbol={stock.symbol}
          exchange={stock.exchange}
          canBeRemoved={stock.canBeRemoved}
          onRemoved={onRemoved}
          label={`Actions for ${stock.symbol}`}
        />
      </header>

      <StatRow>
        <StatCard label={t("inv.detail.sharesHeld")} value={formatShares(position.sharesHeld)} />
        <StatCard label={t("inv.table.averageCost")} value={formatMoney(position.averageCost)} />
        <StatCard
          label={t("inv.detail.currentValue")}
          value={unpriced ? t("inv.table.noPrice") : formatMoney(position.currentValue)}
          hint={
            unpriced
              ? t("inv.detail.noPriceHint")
              : `At ${formatPrice(position.currentPrice)} on ${formatDate(position.currentPriceAsOf ?? "")}`
          }
        />
        <StatCard
          label={t("inv.detail.gain")}
          value={formatGain(position.unrealisedGain)}
          hint={`${formatGain(position.realisedGain)} realised`}
        />
      </StatRow>

      <Panel
        title={t("inv.detail.performance")}
        description={
          chartMode === "price" ? t("inv.price.chartTitle") : t("inv.detail.holdingChartHint")
        }
        aside={
          <div className="flex gap-2" role="group" aria-label={t("inv.detail.chartShows")}>
            <Button
              variant={chartMode === "price" ? "primary" : "outline"}
              aria-pressed={chartMode === "price"}
              onClick={() => {
                setChartMode("price");
              }}
            >
              {t("inv.detail.sharePrice")}
            </Button>
            <Button
              variant={chartMode === "holding" ? "primary" : "outline"}
              aria-pressed={chartMode === "holding"}
              onClick={() => {
                setChartMode("holding");
              }}
            >
              {t("inv.detail.myHolding")}
            </Button>
          </div>
        }
      >
        <PriceChart prices={prices} transactions={transactions} mode={chartMode} />
      </Panel>

      <Panel title={t("inv.detail.transactions")} description={t("inv.detail.transactionsHint")}>
        {transactions.length === 0 ? (
          <p className="text-muted text-sm">{t("inv.detail.nothingRecorded")}</p>
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>{t("inv.detail.date")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.txn.type")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.table.shares")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.table.price")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.detail.fee")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.detail.total")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.detail.recordedBy")}</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="sr-only">{t("inv.table.actions")}</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {transactions.map((txn) => (
                <Table.Row key={txn.id}>
                  <Table.Cell>{formatDate(txn.date)}</Table.Cell>
                  <Table.Cell>
                    {txn.kind === "buy" ? t("inv.txn.buy") : t("inv.txn.sell")}
                  </Table.Cell>
                  <Table.Cell>{formatShares(txn.shares)}</Table.Cell>
                  <Table.Cell>{formatPrice(txn.pricePerShare)}</Table.Cell>
                  <Table.Cell>{formatMoney(txn.fee)}</Table.Cell>
                  <Table.Cell>{formatMoney(txn.total)}</Table.Cell>
                  <Table.Cell>
                    <PersonLabel
                      label={txn.recordedBy}
                      isPerson={txn.recordedById !== null}
                      avatarUrl={members.find((m) => m.userId === txn.recordedById)?.avatarUrl}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <ActionMenu
                      label={t("inv.table.actions")}
                      items={[
                        {
                          label: t("common.edit"),
                          onSelect: () => {
                            setEditingTxn(txn);
                          },
                        },
                        {
                          label: t("common.remove"),
                          destructive: true,
                          onSelect: () => {
                            setDeletingTxn(txn);
                          },
                        },
                      ]}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Panel>

      <Panel
        title={t("inv.detail.recordedPrices")}
        description={
          prices.length > PRICE_ROWS && !allPrices
            ? `The ${String(PRICE_ROWS)} most recent of ${String(prices.length)}.`
            : undefined
        }
        aside={
          prices.length > PRICE_ROWS ? (
            <Button
              onClick={() => {
                setAllPrices((shown) => !shown);
              }}
            >
              {allPrices ? t("inv.detail.showFewer") : `Show all ${String(prices.length)}`}
            </Button>
          ) : undefined
        }
      >
        {prices.length === 0 ? (
          <p className="text-muted text-sm">{t("inv.detail.noPriceYet")}</p>
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>{t("inv.price.asOf")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.table.price")}</Table.HeaderCell>
                <Table.HeaderCell>{t("inv.detail.source")}</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="sr-only">{t("inv.table.actions")}</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {visiblePrices.map((price) => (
                <Table.Row key={price.id}>
                  <Table.Cell>{formatDate(price.asOfDate)}</Table.Cell>
                  <Table.Cell>{formatPrice(price.price)}</Table.Cell>
                  <Table.Cell>
                    {/*
                      "Daily update" is the overnight fetch and "A member" is a stand-in for
                      somebody whose account is gone. Neither is a person, so neither gets a mark —
                      `source` is what distinguishes them, and it is on the row precisely because
                      `recorded_by IS NULL` stopped meaning "the fetch wrote this" in 017.
                    */}
                    <PersonLabel
                      label={
                        price.source === "system"
                          ? t("person.systemFetch")
                          : (price.recordedBy ?? t("inv.detail.aMember"))
                      }
                      isPerson={price.source !== "system" && price.recordedById !== null}
                      avatarUrl={members.find((m) => m.userId === price.recordedById)?.avatarUrl}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    {/* Absent, not disabled, for a system price: there is nothing the member can do
                        about it, and a greyed control invites a click that will never work. */}
                    {price.canDelete && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setDeletingPrice(price.id);
                        }}
                      >
                        {t("common.remove")}
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Panel>

      {/*
        MOUNTED ONLY WHILE OPEN, in every one of these.

        A dialog left mounted keeps its `useState` from the first time it was opened, so the second
        transaction a member records starts with the first one's values still in the fields. Filling
        the ones they care about then silently carries over the ones they do not — a fee, most
        obviously, which lands in the cost basis and moves the average cost without appearing to.

        Found by the E2E spec, which recorded a buy with a fee and then one without and got an
        average cost two cents off. Income mounts its dialogs the same way, for the same reason.
      */}
      {editingTxn !== null && (
        <TransactionForm
          open
          onClose={() => {
            setEditingTxn(null);
            editTransaction.reset();
          }}
          editing={editingTxn}
          pending={editTransaction.isPending}
          error={editTransaction.error?.message ?? null}
          onSubmit={(input) => {
            editTransaction.mutate(
              {
                transactionId: editingTxn.id,
                shares: input.shares,
                pricePerShare: input.pricePerShare,
                fee: input.fee,
                date: input.date,
              },
              {
                onSuccess: () => {
                  setEditingTxn(null);
                },
              },
            );
          }}
        />
      )}

      {deletingTxn !== null && (
        <ConfirmDialog
          open
          onClose={() => {
            setDeletingTxn(null);
            deleteTransaction.reset();
          }}
          title={t("inv.detail.removeTransaction")}
          body={
            <>
              The {deletingTxn?.kind} of {formatShares(deletingTxn?.shares ?? "0")} shares on{" "}
              {deletingTxn ? formatDate(deletingTxn.date) : ""} will stop counting towards this
              position. It is kept in the record rather than erased, and this is refused if a later
              sale depends on it.
            </>
          }
          confirmLabel={t("common.remove")}
          pending={deleteTransaction.isPending}
          error={deleteTransaction.error?.message ?? null}
          onConfirm={() => {
            if (!deletingTxn) return;
            deleteTransaction.mutate(
              { transactionId: deletingTxn.id },
              {
                onSuccess: () => {
                  setDeletingTxn(null);
                },
              },
            );
          }}
        />
      )}

      {deletingPrice !== null && (
        <ConfirmDialog
          open
          onClose={() => {
            setDeletingPrice(null);
            deletePrice.reset();
          }}
          title={t("inv.detail.removePrice")}
          body={t("inv.detail.removePriceHint")}
          confirmLabel={t("common.remove")}
          pending={deletePrice.isPending}
          error={deletePrice.error?.message ?? null}
          onConfirm={() => {
            if (!deletingPrice) return;
            deletePrice.mutate(
              { priceId: deletingPrice },
              {
                onSuccess: () => {
                  setDeletingPrice(null);
                },
              },
            );
          }}
        />
      )}
    </div>
  );
}
